import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

const EditShop = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    businessName: '',
    sellerName: '',
    mobileNumber: '',
    emailId: '',
    mcc: '',
    category: '',
    subCategory: '',
    turnoverType: '',
    acceptanceType: 'OFFLINE',
    ownershipType: '',
    promoterWhatsApp: '',
    city: '',
    district: '',
    state: '',
    stateCode: '',
    pincode: '',
    pan: '',
    gstNumber: '',
    settlementAccountNumber: '',
    settlementAccountIfsc: '',
    latitude: '',
    longitude: '',
    addressLine1: '',
    addressLine2: '',
    llpInCin: '',
    udyogAadhaar: '',
    electricityBillNumber: '',
    electricityBoardCode: '',
    dob: '',
    doi: '',
    promoterAadhaar: '',
    shopLicenceNumber: '',
    shopType: '',
    shopBank: '',
    shopBranch: '',
    documents: {
      gstCertificate: null,
      promoterAadhaarDocument: null,
      promoterAadhaarDocumentName: '',
      shopLicenceDocument: null,
      shopLicenceDocumentName: '',
      gstCertificateName: '',
      panDocument: null,
      panDocumentName: '',
      udyogAadhaarDocument: null,
      udyogAadhaarDocumentName: '',
      electricityBill: null,
      electricityBillName: '',
      incorporationCertificate: null,
      incorporationCertificateName: '',
    },
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessages, setSnackbarMessages] = useState([]);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [electricityBoards, setElectricityBoards] = useState([]);

  const turnoverTypes = ['SMALL', 'LARGE'];
  const ownershipTypes = [
    'PROPRIETARY',
    'PARTNERSHIP',
    'PRIVATE',
    'PUBLIC',
    'LLP',
    'SOCIETY',
    'TRUST',
    'GOVT',
    'HUF',
    'BOI',
    'AOP',
    'AJP',
  ];
  const shopTypes = ['Retail', 'Wholesale', 'Service', 'Manufacturing', 'E-commerce', 'Other'];

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        const decodedToken = jwtDecode(token);
        const userId = decodedToken.userId;

        // Fetch shop data
        const shopResponse = await axios.get(`${API_BASE_URL}/shops/${userId}/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const shop = shopResponse.data;
        setFormData({
          businessName: shop.businessName || '',
          sellerName: shop.sellerName || '',
          mobileNumber: shop.mobileNumber || '',
          emailId: shop.emailId || '',
          mcc: shop.mcc || '',
          category: shop.category || '',
          subCategory: shop.subCategory || '',
          turnoverType: shop.turnoverType || '',
          acceptanceType: shop.acceptanceType || 'OFFLINE',
          ownershipType: shop.ownershipType || '',
          promoterWhatsApp: shop.promoterWhatsApp || '',
          city: shop.city || '',
          district: shop.district || '',
          state: shop.state || '',
          stateCode: shop.stateCode || '',
          pincode: shop.pincode || '',
          pan: shop.pan || '',
          gstNumber: shop.gstNumber || '',
          settlementAccountNumber: shop.settlementAccountNumber || '',
          settlementAccountIfsc: shop.settlementAccountIfsc || '',
          latitude: shop.latitude || '',
          longitude: shop.longitude || '',
          addressLine1: shop.addressLine1 || '',
          addressLine2: shop.addressLine2 || '',
          llpInCin: shop.llpInCin || '',
          udyogAadhaar: shop.udyogAadhaar || '',
          electricityBillNumber: shop.electricityBillNumber || '',
          electricityBoardCode: shop.electricityBoardCode || '',
          dob: shop.dob || '',
          doi: shop.doi || '',
          promoterAadhaar: shop.promoterAadhaar || '',
          shopLicenceNumber: shop.shopLicenceNumber || '',
          shopType: shop.shopType || '',
          shopBank: shop.shopBank || '',
          shopBranch: shop.shopBranch || '',
          documents: {
            gstCertificate: shop.documents?.gstCertificate || null,
            promoterAadhaarDocument: shop.documents?.aadhaarDocument || null,
            promoterAadhaarDocumentName: shop.documents?.aadhaarDocumentName || '',
            shopLicenceDocument: shop.documents?.shopLicence || null,
            shopLicenceDocumentName: shop.documents?.shopLicenceName || '',
            gstCertificateName: shop.documents?.gstCertificateName || '',
            panDocument: shop.documents?.panDocument || null,
            panDocumentName: shop.documents?.panDocumentName || '',
            udyogAadhaarDocument: shop.documents?.udyogAadhaar || null,
            udyogAadhaarDocumentName: shop.documents?.udyogAadhaarName || '',
            electricityBill: shop.documents?.electricityBill || null,
            electricityBillName: shop.documents?.electricityBillName || '',
            incorporationCertificate: shop.documents?.incorporationCertificate || null,
            incorporationCertificateName: shop.documents?.incorporationCertificateName || '',
          },
        });

        // Fetch categories, states, and electricity boards
        const [categoriesRes, statesRes, boardsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/mcc/categories`),
          axios.get(`${API_BASE_URL}/states`),
          axios.get(`${API_BASE_URL}/electricity-boards`),
        ]);
        setCategories(categoriesRes.data);
        setStates(statesRes.data);
        setElectricityBoards(boardsRes.data);

        // Fetch subcategories if category exists
        if (shop.category) {
          const subCatRes = await axios.get(
            `${API_BASE_URL}/mcc/categories/${encodeURIComponent(shop.category)}/subcategories`
          );
          setSubCategories(subCatRes.data);
        }

        // Fetch districts if state is Kerala
        if (shop.state === 'Kerala') {
          const districtsRes = await axios.get(`${API_BASE_URL}/districts`);
          setDistricts(districtsRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setSnackbarMessages(['Failed to load shop data. Please try again.']);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [id]);

  useEffect(() => {
    if (formData.category) {
      const fetchSubCategories = async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/mcc/categories/${encodeURIComponent(formData.category)}/subcategories`
          );
          setSubCategories(response.data);
        } catch (error) {
          console.error('Error fetching subcategories:', error);
        }
      };
      fetchSubCategories();
    } else {
      setSubCategories([]);
    }
  }, [formData.category]);

  useEffect(() => {
    if (formData.subCategory) {
      const fetchMCCCode = async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/mcc/subcategories/${encodeURIComponent(formData.subCategory)}`
          );
          setFormData((prevData) => ({
            ...prevData,
            mcc: response.data.mccCode,
          }));
        } catch (error) {
          console.error('Error fetching MCC code:', error);
        }
      };
      fetchMCCCode();
    }
  }, [formData.subCategory]);

  useEffect(() => {
    if (formData.state) {
      setFormData((prevData) => ({
        ...prevData,
        stateCode: formData.state,
      }));
    }
  }, [formData.state]);

  useEffect(() => {
    if (formData.state === 'Kerala') {
      const fetchDistricts = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/districts`);
          setDistricts(response.data);
        } catch (error) {
          console.error('Error fetching districts:', error);
        }
      };
      fetchDistricts();
    } else {
      setDistricts([]);
    }
  }, [formData.state]);

  const handleInputChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[field];
      return updatedErrors;
    });
  };

  const handleFileUpload = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: 'File size should be less than 2MB',
        }));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prevData) => ({
          ...prevData,
          documents: {
            ...prevData.documents,
            [field]: reader.result,
            [`${field}Name`]: file.name,
          },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileRemove = (field) => {
    setFormData((prevData) => ({
      ...prevData,
      documents: {
        ...prevData.documents,
        [field]: null,
        [`${field}Name`]: '',
      },
    }));
  };

  const validate = () => {
    const newErrors = {};
    const {
      businessName,
      sellerName,
      mobileNumber,
      emailId,
      mcc,
      turnoverType,
      shopLicenceNumber,
      promoterWhatsApp,
      ownershipType,
      promoterAadhaar,
      city,
      district,
      pincode,
      pan,
      gstNumber,
      settlementAccountNumber,
      settlementAccountIfsc,
      latitude,
      longitude,
      addressLine1,
      addressLine2,
      llpInCin,
      udyogAadhaar,
      electricityBillNumber,
      electricityBoardCode,
      dob,
      doi,
      shopType,
      shopBank,
      shopBranch,
      documents,
    } = formData;

    if (!businessName.trim()) newErrors.businessName = 'Business Name is required';
    if (!sellerName.trim()) newErrors.sellerName = 'Seller Name is required';
    if (!mobileNumber.trim() && !emailId.trim()) {
      newErrors.mobileNumber = 'Either Mobile Number or Email ID is required';
      newErrors.emailId = 'Either Mobile Number or Email ID is required';
    } else {
      if (mobileNumber.trim() && !/^[6-9]\d{9}$/.test(mobileNumber)) {
        newErrors.mobileNumber = 'Invalid Mobile Number';
      }
      if (promoterWhatsApp && !/^[6-9]\d{9}$/.test(promoterWhatsApp)) {
        newErrors.promoterWhatsApp = 'Invalid WhatsApp Number';
      }
      if (emailId.trim() && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,4}$/.test(emailId)) {
        newErrors.emailId = 'Invalid Email ID';
      }
    }
    if (!mcc.trim()) newErrors.mcc = 'MCC is required';
    if (!turnoverType) newErrors.turnoverType = 'Turnover Type is required';
    if (!ownershipType) newErrors.ownershipType = 'Ownership Type is required';
    if (!shopType) newErrors.shopType = 'Shop Type is required';
    if (!shopBank.trim()) newErrors.shopBank = 'Shop Bank is required';
    if (!settlementAccountNumber.trim()) newErrors.settlementAccountNumber = 'Account Number is required';
    if (!settlementAccountIfsc.trim()) newErrors.settlementAccountIfsc = 'Account IFSC is required';
    if (!shopBranch.trim()) newErrors.shopBranch = 'Shop Branch is required';
    if (!city.trim()) newErrors.city = 'City is required';
    if (formData.state === 'Kerala' && !district) newErrors.district = 'District is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!pincode.trim()) newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(pincode)) newErrors.pincode = 'Invalid Pincode';
    if (!latitude.trim()) newErrors.latitude = 'Latitude is required';
    else if (!/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/.test(latitude)) newErrors.latitude = 'Invalid Latitude';
    if (!longitude.trim()) newErrors.longitude = 'Longitude is required';
    else if (!/^[-+]?((1[0-7]\d)|([1-9]?\d))(\.\d+)?$/.test(longitude)) newErrors.longitude = 'Invalid Longitude';
    if (!addressLine1.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!addressLine2.trim()) newErrors.addressLine2 = 'Address Line 2 is required';

    if (pan.trim()) {
      if (!/[A-Z]{5}[0-9]{4}[A-Z]{1}/.test(pan)) newErrors.pan = 'Invalid PAN';
      if (!documents.panDocument) newErrors.panDocument = 'PAN Document is required';
    }
    if (gstNumber.trim()) {
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstNumber)) {
        newErrors.gstNumber = 'Invalid GST Number';
      }
      if (!documents.gstCertificate) newErrors.gstCertificate = 'GST Certificate is required';
      if (!pan.trim()) newErrors.pan = 'PAN is required when GST Number is provided';
    }
    if (ownershipType === 'PROPRIETARY') {
      if (!dob.trim()) newErrors.dob = 'Date of Birth is required';
      else {
        const [day, month, year] = dob.split('/');
        const dobDate = new Date(`${year}-${month}-${day}`);
        const age = Math.floor((Date.now() - dobDate) / (365.25 * 24 * 60 * 60 * 1000));
        if (age < 18) newErrors.dob = 'Age should be at least 18 years';
      }
    } else {
      if (!doi.trim()) newErrors.doi = 'Date of Incorporation is required';
      else {
        const [day, month, year] = doi.split('/');
        const doiDate = new Date(`${year}-${month}-${day}`);
        if (doiDate > new Date()) newErrors.doi = 'Date of Incorporation cannot be in the future';
      }
      if (['PRIVATE', 'PUBLIC', 'LLP'].includes(ownershipType)) {
        if (!llpInCin.trim()) newErrors.llpInCin = 'LLP IN/CIN is required';
        if (!documents.incorporationCertificate) {
          newErrors.incorporationCertificate = 'Incorporation Certificate is required';
        }
      }
    }
    if (udyogAadhaar.trim()) {
      if (!/^[a-zA-Z0-9]{12}$/.test(udyogAadhaar)) newErrors.udyogAadhaar = 'Invalid Udyog Aadhaar';
      if (!documents.udyogAadhaarDocument) newErrors.udyogAadhaarDocument = 'Udyog Aadhaar Document is required';
    }
    if (promoterAadhaar.trim()) {
      if (!/^[a-zA-Z0-9]{12}$/.test(promoterAadhaar)) newErrors.promoterAadhaar = 'Invalid Promoter Aadhaar';
      if (!documents.promoterAadhaarDocument) newErrors.promoterAadhaarDocument = 'Promoter Aadhaar Document is required';
    }
    if (shopLicenceNumber.trim()) {
      if (!/^[a-zA-Z0-9]{12}$/.testnershipLicenceNumber) newErrors.shopLicenceNumber = 'Invalid Shop Licence Number';
      if (!documents.shopLicenceDocument) newErrors.shopLicenceDocument = 'Shop Licence Document is required';
    }
    if (electricityBillNumber.trim()) {
      if (!documents.electricityBill) newErrors.electricityBill = 'Electricity Bill is required';
      if (!electricityBoardCode.trim()) newErrors.electricityBoardCode = 'Electricity Board Code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      setConfirmationDialogOpen(true);
    }
  };

  const handleConfirmSubmit = async () => {
    setConfirmationDialogOpen(false);
    setLoading(true);

    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.userId;

      const requestData = { ...formData };
      await axios.put(`${API_BASE_URL}/shops/${userId}/${id}`, requestData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSnackbarMessages(['Shop updated successfully!']);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate('/verify-documents');
    } catch (error) {
      console.error('Error updating shop:', error);
      setSnackbarMessages(
        error.response?.status === 400 && error.response.data.errors
          ? error.response.data.errors
          : ['Failed to update shop. Please try again.']
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
    {/* Place BackButton outside the Grid */}
    <BackButton />
    <br/>  <br/>
    <Box sx={{ width: '90%', mx: 'auto', mt: 4 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Edit Shop
      </Typography>
      <Card sx={{ mb: 4 }}>
        <CardContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <Box component="form" noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6">Business Details</Typography>
                  <TextField
                    label="Business Name"
                    value={formData.businessName}
                    onChange={(e) => handleInputChange('businessName', e.target.value)}
                    error={!!errors.businessName}
                    helperText={errors.businessName}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Seller Name"
                    value={formData.sellerName}
                    onChange={(e) => handleInputChange('sellerName', e.target.value)}
                    error={!!errors.sellerName}
                    helperText={errors.sellerName}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Mobile Number"
                    value={formData.mobileNumber}
                    onChange={(e) => handleInputChange('mobileNumber', e.target.value)}
                    error={!!errors.mobileNumber}
                    helperText={errors.mobileNumber}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="WhatsApp Number"
                    value={formData.promoterWhatsApp}
                    onChange={(e) => handleInputChange('promoterWhatsApp', e.target.value)}
                    error={!!errors.promoterWhatsApp}
                    helperText={errors.promoterWhatsApp}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Email ID"
                    value={formData.emailId}
                    onChange={(e) => handleInputChange('emailId', e.target.value)}
                    error={!!errors.emailId}
                    helperText={errors.emailId}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Promoter Aadhaar"
                    value={formData.promoterAadhaar}
                    onChange={(e) => handleInputChange('promoterAadhaar', e.target.value)}
                    error={!!errors.promoterAadhaar}
                    helperText={errors.promoterAadhaar}
                    fullWidth
                    margin="normal"
                  />
                  {formData.promoterAadhaar && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button variant="contained" component="label">
                        Upload Aadhaar Document
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          hidden
                          onChange={(e) => handleFileUpload(e, 'promoterAadhaarDocument')}
                        />
                      </Button>
                      {formData.documents.promoterAadhaarDocumentName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                          <Typography>{formData.documents.promoterAadhaarDocumentName}</Typography>
                          <IconButton onClick={() => handleFileRemove('promoterAadhaarDocument')}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}
                      {errors.promoterAadhaarDocument && (
                        <Typography color="error">{errors.promoterAadhaarDocument}</Typography>
                      )}
                    </Box>
                  )}
                  <FormControl fullWidth margin="normal" error={!!errors.category}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      label="Category"
                    >
                      {categories.map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {subCategories.length > 0 && (
                    <FormControl fullWidth margin="normal" error={!!errors.subCategory}>
                      <InputLabel>Sub Category</InputLabel>
                      <Select
                        value={formData.subCategory}
                        onChange={(e) => handleInputChange('subCategory', e.target.value)}
                        label="Sub Category"
                      >
                        {subCategories.map((subCategory) => (
                          <MenuItem key={subCategory} value={subCategory}>
                            {subCategory}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <TextField
                    label="MCC"
                    value={formData.mcc}
                    onChange={(e) => handleInputChange('mcc', e.target.value)}
                    error={!!errors.mcc}
                    helperText={errors.mcc}
                    fullWidth
                    margin="normal"
                    disabled
                  />
                  <FormControl fullWidth margin="normal" error={!!errors.turnoverType}>
                    <InputLabel>Turnover Type</InputLabel>
                    <Select
                      value={formData.turnoverType}
                      onChange={(e) => handleInputChange('turnoverType', e.target.value)}
                      label="Turnover Type"
                    >
                      {turnoverTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Acceptance Type"
                    value={formData.acceptanceType}
                    fullWidth
                    margin="normal"
                    disabled
                  />
                  <FormControl fullWidth margin="normal" error={!!errors.ownershipType}>
                    <InputLabel>Ownership Type</InputLabel>
                    <Select
                      value={formData.ownershipType}
                      onChange={(e) => handleInputChange('ownershipType', e.target.value)}
                      label="Ownership Type"
                    >
                      {ownershipTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth margin="normal" error={!!errors.shopType}>
                    <InputLabel>Shop Type</InputLabel>
                    <Select
                      value={formData.shopType}
                      onChange={(e) => handleInputChange('shopType', e.target.value)}
                      label="Shop Type"
                    >
                      {shopTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.ownershipType === 'PROPRIETARY' && (
                    <TextField
                      label="Date of Birth (DD/MM/YYYY)"
                      value={formData.dob}
                      onChange={(e) => handleInputChange('dob', e.target.value)}
                      error={!!errors.dob}
                      helperText={errors.dob}
                      fullWidth
                      margin="normal"
                    />
                  )}
                  {formData.ownershipType !== 'PROPRIETARY' && (
                    <>
                      <TextField
                        label="Date of Incorporation (DD/MM/YYYY)"
                        value={formData.doi}
                        onChange={(e) => handleInputChange('doi', e.target.value)}
                        error={!!errors.doi}
                        helperText={errors.doi}
                        fullWidth
                        margin="normal"
                      />
                      {['PRIVATE', 'PUBLIC', 'LLP'].includes(formData.ownershipType) && (
                        <>
                          <TextField
                            label="LLP IN/CIN"
                            value={formData.llpInCin}
                            onChange={(e) => handleInputChange('llpInCin', e.target.value)}
                            error={!!errors.llpInCin}
                            helperText={errors.llpInCin}
                            fullWidth
                            margin="normal"
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button variant="contained" component="label">
                              Upload Incorporation Certificate
                              <input
                                type="file"
                                accept="application/pdf,image/*"
                                hidden
                                onChange={(e) => handleFileUpload(e, 'incorporationCertificate')}
                              />
                            </Button>
                            {formData.documents.incorporationCertificateName && (
                              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                <Typography>{formData.documents.incorporationCertificateName}</Typography>
                                <IconButton onClick={() => handleFileRemove('incorporationCertificate')}>
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            )}
                            {errors.incorporationCertificate && (
                              <Typography color="error">{errors.incorporationCertificate}</Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </>
                  )}
                  <TextField
                    label="PAN"
                    value={formData.pan}
                    onChange={(e) => handleInputChange('pan', e.target.value)}
                    error={!!errors.pan}
                    helperText={errors.pan}
                    fullWidth
                    margin="normal"
                  />
                  {formData.pan && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button variant="contained" component="label">
                        Upload PAN Document
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          hidden
                          onChange={(e) => handleFileUpload(e, 'panDocument')}
                        />
                      </Button>
                      {formData.documents.panDocumentName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                          <Typography>{formData.documents.panDocumentName}</Typography>
                          <IconButton onClick={() => handleFileRemove('panDocument')}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}
                      {errors.panDocument && (
                        <Typography color="error">{errors.panDocument}</Typography>
                      )}
                    </Box>
                  )}
                  <TextField
                    label="GST Number"
                    value={formData.gstNumber}
                    onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                    error={!!errors.gstNumber}
                    helperText={errors.gstNumber}
                    fullWidth
                    margin="normal"
                  />
                  {formData.gstNumber && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button variant="contained" component="label">
                        Upload GST Certificate
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          hidden
                          onChange={(e) => handleFileUpload(e, 'gstCertificate')}
                        />
                      </Button>
                      {formData.documents.gstCertificateName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                          <Typography>{formData.documents.gstCertificateName}</Typography>
                          <IconButton onClick={() => handleFileRemove('gstCertificate')}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}
                      {errors.gstCertificate && (
                        <Typography color="error">{errors.gstCertificate}</Typography>
                      )}
                    </Box>
                  )}
                  <TextField
                    label="Shop Licence Number"
                    value={formData.shopLicenceNumber}
                    onChange={(e) => handleInputChange('shopLicenceNumber', e.target.value)}
                    error={!!errors.shopLicenceNumber}
                    helperText={errors.shopLicenceNumber}
                    fullWidth
                    margin="normal"
                  />
                  {formData.shopLicenceNumber && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button variant="contained" component="label">
                        Upload Shop Licence
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          hidden
                          onChange={(e) => handleFileUpload(e, 'shopLicenceDocument')}
                        />
                      </Button>
                      {formData.documents.shopLicenceDocumentName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                          <Typography>{formData.documents.shopLicenceDocumentName}</Typography>
                          <IconButton onClick={() => handleFileRemove('shopLicenceDocument')}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}
                      {errors.shopLicenceDocument && (
                        <Typography color="error">{errors.shopLicenceDocument}</Typography>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="h6">Address Details</Typography>
                  <TextField
                    label="City"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    error={!!errors.city}
                    helperText={errors.city}
                    fullWidth
                    margin="normal"
                  />
                  <FormControl fullWidth margin="normal" error={!!errors.state}>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      label="State"
                    >
                      {states.map((state) => (
                        <MenuItem key={state.id} value={state.state_name}>
                          {state.state_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.state === 'Kerala' ? (
                    <FormControl fullWidth margin="normal" error={!!errors.district}>
                      <InputLabel>District</InputLabel>
                      <Select
                        value={formData.district}
                        onChange={(e) => handleInputChange('district', e.target.value)}
                        label="District"
                      >
                        {districts.map((district) => (
                          <MenuItem key={district._id} value={district.name}>
                            {district.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      label="District"
                      value={formData.district}
                      onChange={(e) => handleInputChange('district', e.target.value)}
                      error={!!errors.district}
                      helperText={errors.district}
                      fullWidth
                      margin="normal"
                      disabled={!formData.state}
                    />
                  )}
                  <TextField
                    label="Pincode"
                    value={formData.pincode}
                    onChange={(e) => handleInputChange('pincode', e.target.value)}
                    error={!!errors.pincode}
                    helperText={errors.pincode}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Latitude"
                    value={formData.latitude}
                    onChange={(e) => handleInputChange('latitude', e.target.value)}
                    error={!!errors.latitude}
                    helperText={errors.latitude}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Longitude"
                    value={formData.longitude}
                    onChange={(e) => handleInputChange('longitude', e.target.value)}
                    error={!!errors.longitude}
                    helperText={errors.longitude}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Address Line 1"
                    value={formData.addressLine1}
                    onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                    error={!!errors.addressLine1}
                    helperText={errors.addressLine1}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Address Line 2"
                    value={formData.addressLine2}
                    onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                    error={!!errors.addressLine2}
                    helperText={errors.addressLine2}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Bank"
                    value={formData.shopBank}
                    onChange={(e) => handleInputChange('shopBank', e.target.value)}
                    error={!!errors.shopBank}
                    helperText={errors.shopBank}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Branch"
                    value={formData.shopBranch}
                    onChange={(e) => handleInputChange('shopBranch', e.target.value)}
                    error={!!errors.shopBranch}
                    helperText={errors.shopBranch}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Account Number"
                    value={formData.settlementAccountNumber}
                    onChange={(e) => handleInputChange('settlementAccountNumber', e.target.value)}
                    error={!!errors.settlementAccountNumber}
                    helperText={errors.settlementAccountNumber}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Account IFSC"
                    value={formData.settlementAccountIfsc}
                    onChange={(e) => handleInputChange('settlementAccountIfsc', e.target.value)}
                    error={!!errors.settlementAccountIfsc}
                    helperText={errors.settlementAccountIfsc}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    label="Udyog Aadhaar"
                    value={formData.udyogAadhaar}
                    onChange={(e) => handleInputChange('udyogAadhaar', e.target.value)}
                    error={!!errors.udyogAadhaar}
                    helperText={errors.udyogAadhaar}
                    fullWidth
                    margin="normal"
                  />
                  {formData.udyogAadhaar && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button variant="contained" component="label">
                        Upload Udyog Aadhaar Document
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          hidden
                          onChange={(e) => handleFileUpload(e, 'udyogAadhaarDocument')}
                        />
                      </Button>
                      {formData.documents.udyogAadhaarDocumentName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                          <Typography>{formData.documents.udyogAadhaarDocumentName}</Typography>
                          <IconButton onClick={() => handleFileRemove('udyogAadhaarDocument')}>
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      )}
                      {errors.udyogAadhaarDocument && (
                        <Typography color="error">{errors.udyogAadhaarDocument}</Typography>
                      )}
                    </Box>
                  )}
                  <TextField
                    label="Electricity Bill Number"
                    value={formData.electricityBillNumber}
                    onChange={(e) => handleInputChange('electricityBillNumber', e.target.value)}
                    error={!!errors.electricityBillNumber}
                    helperText={errors.electricityBillNumber}
                    fullWidth
                    margin="normal"
                  />
                  {formData.electricityBillNumber && (
                    <>
                      <FormControl fullWidth margin="normal" error={!!errors.electricityBoardCode}>
                        <InputLabel>Electricity Board</InputLabel>
                        <Select
                          value={formData.electricityBoardCode}
                          onChange={(e) => handleInputChange('electricityBoardCode', e.target.value)}
                          label="Electricity Board"
                        >
                          {electricityBoards.map((board) => (
                            <MenuItem key={board.boardCode} value={board.boardCode}>
                              {board.boardName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button variant="contained" component="label">
                          Upload Electricity Bill
                          <input
                            type="file"
                            accept="application/pdf,image/*"
                            hidden
                            onChange={(e) => handleFileUpload(e, 'electricityBill')}
                          />
                        </Button>
                        {formData.documents.electricityBillName && (
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                            <Typography>{formData.documents.electricityBillName}</Typography>
                            <IconButton onClick={() => handleFileRemove('electricityBill')}>
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        )}
                        {errors.electricityBill && (
                          <Typography color="error">{errors.electricityBill}</Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
        <Box sx={{ p: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSubmit} fullWidth disabled={loading}>
            Update Shop
          </Button>
        </Box>
      </Card>

      <Dialog open={confirmationDialogOpen} onClose={() => setConfirmationDialogOpen(false)}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to update the shop details?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbarOpen} autoHideDuration={8000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessages.join('\n')}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default EditShop;